
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import useLocale from 'next-translate/useTranslation';
import React from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';
const PrivacyPolicy = () => {
    // Opened from inside the iOS app (?from=app): drop the marketing nav
    // and footer, both of which link to /pricing — a purchase call to
    // action App Store guideline 3.1.3(b) does not allow in-app.
    const router = useRouter();
    const isFromApp = router.query.from === 'app';
    const { t } = useLocale('common');
    return (<main>
      <Seo templateTitle='Privacy Policy' image={`https://easy-peasy.ai/api/og-template?title=${t('Privacy Policy')}`}/>
      <div className='relative bg-white dark:bg-slate-900'>
        <div className='mx-auto'>
          <div className='relative z-10 bg-white dark:bg-slate-900 sm:pb-8'>
            {!isFromApp && <LandingPageNavigation />}
          </div>
        </div>
      </div>
      <section className='bg-white px-4 py-16 dark:bg-slate-900 sm:px-6 lg:px-8'>
        <div className='prose prose-lg prose-indigo mx-auto mt-6 text-gray-500 dark:text-slate-400'>
          <h1 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Privacy Policy
          </h1>

          <p className='text-2xl'>
            At Easy-Peasy.AI, your privacy and the protection of your data is
            important to us. All personal data that is collected is done so in
            accordance with the requirements of applicable law.
          </p>
          <hr />
          <p>
            The Website/Services (https://easy-peasy.ai or such other domain
            name that may be allocated from time to time) is operated by
            Easy-Peasy.AI Pte Ltd.
          </p>
          <p>
            Easy-Peasy.AI (&quot;We,&quot; &quot;Our,&quot; or &quot;Us&quot;)
            is committed to protecting your privacy. This Privacy Policy
            explains how your personal information is collected, used, and
            disclosed by Easy-Peasy.AI.
          </p>
          <p>
            This Privacy Policy applies to our website, and its associated
            subdomains (collectively, our &quot;Website/Services&quot;). By
            accessing the Website/Services, registering an account with the
            Website/Services or placing an order with the Website/Services you
            signify that you have read, understood, and agree to our collection,
            storage, use, and disclosure of your personal information as
            described in this Privacy Policy and our Terms of Use/Service.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Definitions and key terms
          </h2>
          <p>
            To help explain things as clearly as possible in this Privacy
            Policy, as the following definitions shall apply within this Privacy
            Policy:
          </p>
          <ul>
            <li>
              <strong>Cookie:</strong> small amount of data generated by a
              website and saved by your web browser. It is used to identify your
              browser, provide analytics, remember information about you such as
              your language preference or login information.
            </li>
            <li>
              <strong>Company:</strong> when this policy mentions
              &quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or
              &quot;our,&quot; it refers to Easy-Peasy.AI Pte Ltd, that is
              responsible for your information under this Privacy Policy.
            </li>
            <li>
              <strong>Corporate Affiliate:</strong> means any person or entity
              which directly or indirectly controls, is controlled by or is
              under common control with Easy-Peasy.AI, whether by ownership or
              otherwise.
            </li>
            <li>
              <strong>Country:</strong> where Easy-Peasy.AI or the
              owners/founders of Easy-Peasy.AI are based, in this case is
              Singapore
            </li>
            <li>
              <strong>Customer:</strong> refers to the company, organization or
              person that signs up to use the Easy-Peasy.AI Services to manage
              the relationships with your consumers or service users.
            </li>
            <li>
              <strong>Device:</strong> any internet connected device such as a
              phone, tablet, computer or any other device that can be used to
              visit Easy-Peasy.AI and use the services.
            </li>
            <li>
              <strong>IP address:</strong> Every device connected to the
              Internet is assigned a number known as an Internet protocol (IP)
              address. These numbers are usually assigned in geographic blocks.
              An IP address can often be used to identify the location from
              which a device is connecting to the Internet.
            </li>
            <li>
              <strong>Personnel:</strong> refers to those individuals who are
              employed by Easy-Peasy.AI or are under contract to perform a
              service on behalf of one of the parties.
            </li>
            <li>
              <strong>Personal Data:</strong> any information that directly,
              indirectly, or in connection with other information — including a
              personal identification number — allows for the identification or
              identifiability of a natural person.
            </li>
            <li>
              <strong>Services:</strong> refers to the services provided by
              Easy-Peasy.AI on the Website, including any APIs which may be made
              available.
            </li>
            <li>
              <strong>Third-party service:</strong> refers to advertisers,
              contest sponsors, promotional and marketing partners, and others
              who provide our content or whose products or services we think may
              interest you.
            </li>
            <li>
              <strong>Website:</strong> Easy-Peasy.AI&apos;s site, which can be
              accessed via this URL: https://easy-peasy.ai/
            </li>
            <li>
              <strong>You:</strong> a person or entity that is registered with
              Easy-Peasy.AI to use the Services.
            </li>
          </ul>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Information automatically collected
          </h2>
          <p>
            There is some information (such as, but not limited to, your
            Internet Protocol (IP) address and/or browser and device
            characteristics) which is collected automatically when you visit the
            Website. This information may be used to connect your computer to
            the Internet or to any of the Services via the Internet. Other
            information collected automatically could be a login, e-mail
            address, password, computer and connection information such as
            browser plug-in types and versions and time zone setting, operating
            systems and platforms, purchase history, the full Uniform Resource
            Locator (URL) clickstream to, through and from our Website that may
            include date and time; cookie number; parts of the site you viewed
            or searched for; and the phone number you used to call our Customer
            Services. We may also use browser data such as cookies, Flash
            cookies (also known as Flash Local Shared Objects) or similar data
            on certain parts of our Website for fraud prevention and other
            purposes. During your visits, we may use software tools such as (but
            not limited to) JavaScript to measure and collect session
            information including page response times, download errors, length
            of visits to certain pages, page interaction information (such as
            scrolling, clicks, and mouse-overs), and methods used to browse away
            from the page. We may also collect technical information to help us
            identify your Device for fraud prevention and diagnostic purposes.
          </p>
          <p>
            We automatically collect certain information when you visit, use or
            navigate the Website. This information does not reveal your specific
            identity (like your name or contact information) but may include
            Device and usage information, such as your IP address, browser and
            device characteristics, operating system, language preferences,
            referring URLs, Device name, country, location, information about
            who and when you use our and other technical information. This
            information is primarily needed to maintain the security and
            operation of our platform, and for our internal analytics and
            reporting purposes.
          </p>
          <p>
            By accessing the Website/Services, registering an account with the
            Website/Services or placing an order with the Website/Services, you
            consent and agree to the collection, storage, processing and
            deletion of all of the information referred to above.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Sale of Business
          </h2>
          <p>
            We reserve the right to transfer information to a third party in the
            event of a sale, merger or other transfer of all or substantially
            all of the assets of Easy-Peasy.AI or any of its Corporate
            Affiliates (as defined herein), or that portion of Easy-Peasy.AI or
            any of its Corporate Affiliates to which the Website/Services
            relate, or in the event that we discontinue our business or file a
            petition or have filed against us a petition in bankruptcy,
            reorganization or similar proceeding, provided that the third party
            agrees to adhere to the terms of this Privacy Policy. You consent
            and agree to said transfer above. In the event that you do not
            consent/agree to the transfer described above, then you are to
            inform said third party immediately.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Affiliates
          </h2>
          <p>
            We may disclose information (including personal information) about
            you to our Corporate Affiliates. Any information relating to you
            that we disclose to our Corporate Affiliates will be treated by
            those Corporate Affiliates in accordance with the terms of this
            Privacy Policy. You consent and agree to said disclosure above.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Use of AI Services, Prompts and Data Processing
          </h2>
          <p>
            When you use our Website/Services, any prompts, queries, or
            information you input (collectively &quot;User Inputs&quot;) may be
            transmitted to third-party artificial intelligence service
            providers, including but not limited to OpenAI, Claude, Google, or
            other AI or information processing service providers (collectively
            &quot;AI Service Providers&quot;). Your User Inputs may contain
            personal information that you voluntarily provide. By using our
            Website and submitting User Inputs, you explicitly consent to:
          </p>
          <ul>
            <li>
              The transmission of your User Inputs, including any personal
              information contained therein, to our AI Service Providers;
            </li>
            <li>
              The processing, analysis, and storage of such information by these
              AI Service Providers in accordance with their respective privacy
              policies and terms of service;
            </li>
            <li>
              The potential retention of your User Inputs by these AI Service
              Providers for purposes including service improvement, compliance
              with applicable laws, and maintaining service safety.
            </li>
          </ul>
          <p>
            This consent constitutes the lawful basis for such processing under
            applicable data protection regulations, including but not limited to
            the General Data Protection Regulation (GDPR) and other relevant
            privacy laws.
          </p>
          <p>
            We have carefully selected our AI Service Providers and require them
            to maintain appropriate security measures to protect your data.
            However, please be aware that once your User Inputs are transmitted
            to these providers, they will be subject to the respective
            provider&apos;s privacy policies and data handling practices.
          </p>
          <p>
            While we implement reasonable measures to protect your information,
            please be mindful not to include sensitive personal information,
            confidential business information, or other protected data in your
            User Inputs unless absolutely necessary. We cannot guarantee that AI
            Service Providers will not retain or use such information in
            accordance with their own policies.
          </p>
          <p>
            We do not use your User Inputs to train our own AI models without
            your explicit consent separate from this general consent provision.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Governing Law
          </h2>
          <p>
            This Privacy Policy is governed by and shall be interpreted under
            the laws of Singapore. You consent to the exclusive jurisdiction of
            the Supreme Court of Singapore in connection with any action or
            dispute arising between the parties under or in connection with this
            Privacy Policy except for those individuals who may have rights to
            make claims under Privacy Shield, or the Swiss-US framework.
          </p>
          <p>
            Your use of the Website/Services may also be subject to other local,
            state, national, or international laws.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Your Consent
          </h2>
          <p>
            We&apos;ve updated our Privacy Policy to provide you with requisite
            transparency into what is being set when you visit/use our
            Website/Services and how it is being used.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Cookies
          </h2>
          <p>
            Easy-Peasy.AI uses Cookies to identify the areas of our website that
            you have visited. A Cookie is a small piece of data stored on your
            computer or mobile device by your web browser. We use Cookies to
            enhance the performance and functionality of the Website/Services
            but are non-essential to their use. However, without these cookies,
            certain functionality like videos may become unavailable or you
            would be required to enter your login details every time you visit
            the website as we would not be able to remember that you had logged
            in previously. Most web browsers can be set to disable the use of
            Cookies. However, if you disable Cookies, you may not be able to
            access functionality on Our Website/Services correctly or at all. We
            never place Personally Identifiable Information in Cookies.
          </p>
          <p>
            Wherever you are located, you may set your browser to block cookies
            and similar technologies, but this action may block our essential
            cookies and prevent our website from functioning properly, and you
            may not be able to fully utilize all of its features and services.
            You should also be aware that you may also lose some saved
            information (e.g. saved login details, site preferences) if you
            block cookies on your browser. Different browsers make different
            controls available to you. Disabling a cookie or category of cookies
            does not delete the cookie(s) from your browser. Should you wish to
            delete a cookie, you will need to do this yourself from within your
            browser; you should visit your browser&apos;s help menu for more
            information.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Remarketing Services
          </h2>
          <p>
            We use remarketing services such as (but not limited to) Google Ads
            Remarketing and Meta Remarketing. What Is remarketing? In digital
            marketing, remarketing (or retargeting) is the practice of serving
            ads across the internet to people who have already visited the
            Website. The Website/Services uses remarketing services to advertise
            to previous visitors across third-party websites, including Google,
            Meta, and other advertising networks. Remarketing enables us to
            display tailored advertisements to users who have previously visited
            the Website, based on their past interactions with the
            Website/Services. When you visit or use the Website/Services,
            third-party vendors (including Google, Meta etc.) may use cookies,
            device identifiers, or similar technologies to collect information
            about your browsing activities. This information is used to serve
            ads that are relevant to your interests and based on your previous
            visits to our Website/Services. These ads may appear on various
            websites and apps across the Internet that are not under our
            control.
          </p>
          <p>
            Further, if you were to input any personally identifiable
            information into any of the forms, input boxes, text boxes, search
            boxes etc. (other than information pertaining to your personal
            profile) or if you were to submit any files containing any
            personally identifiable information, then we cannot be held
            responsible nor liable for any processing or disclosure of any such
            personally identifiable information to any third parties.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Payment Details
          </h2>
          <p>
            In respect to any credit card or other payment processing details
            you have provided us, we commit that this confidential information
            will be stored in a secure manner that is reasonable for a majority
            of websites. Nonetheless, we cannot be held responsible nor liable
            for any breach of security or unauthorized leak of confidential
            information in the event of any unauthorized access/copying/
            destruction/alteration, hack, unauthorized leak, breach of security
            or the like.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Privacy of Minors
          </h2>
          <p>
            The Website/Services are not directed to (nor do we knowingly
            collect or solicit) personal information from children under the age
            of 13. If You are under the age of 13, please do not use our
            Website, access our Services, or provide any personal information to
            us, including (but not limited to) your name, address, telephone
            number, email address or that of any other individual or any other
            information which may lead to any form of personal identification of
            anyone whosoever.
          </p>
          <p>
            In the event that We become aware that We have inadvertently
            collected personal information from a child under the age of 13
            without verified parental consent, We will take immediate steps to
            delete such information from Our records. If You are a parent or
            legal guardian and believe that Your child under the age of 13 has
            provided us with personal information, please contact us at
            support@easy-peasy.ai so that we may take appropriate action.
          </p>
          <p>
            We strongly encourage parents and guardians to monitor their
            children&apos;s online activities and to help enforce this Privacy
            Policy by instructing their children never to provide personal
            information through Our Website/Services without their permission.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Changes To Our Privacy Policy
          </h2>
          <p>
            We may change Our Website, Services and policies, and we may need to
            make changes to this Privacy Policy from time to time so that they
            accurately reflect our Website, Services and policies. Unless
            otherwise required by law, we will notify you (for example, through
            our Website and/or Services) before we make changes to this Privacy
            Policy and give you an opportunity to review them before they go
            into effect. Then, if you continue to use the Website/Services, you
            will be bound by the updated Privacy Policy. If you do not want to
            agree to this or any updated Privacy Policy, you must delete your
            account.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Third-Party Services
          </h2>
          <p>
            We may display, include or make available third-party content
            (including data, information, applications and other products
            services) or provide links to third-party websites or services
            (&quot;Third-Party Services&quot;).
          </p>
          <p>
            You acknowledge and agree that Easy-Peasy.AI shall not be
            responsible for any Third-Party Services, including their accuracy,
            completeness, timeliness, validity, copyright compliance, legality,
            decency, quality or any other aspect thereof. Easy-Peasy.AI does not
            assume and shall not have any liability or responsibility to you or
            any other person or entity for any Third-Party Services.
          </p>
          <p>
            Third-Party Services and links thereto are provided solely as a
            convenience to you and you access and use them entirely at your own
            risk and subject to such third parties&apos; terms and conditions.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Analytics Tools
          </h2>
          <p>
            We use analytics tools on our Website/Services such as (but not
            limited to) Google Analytics and Meta Pixel. These analytics tools
            allow Us to measure the effectiveness of our advertising on the
            Website by understanding the actions users take on the Website. It
            is important for you to stay updated on all respective privacy
            policies of these tools, which may change from time to time.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Use of Google User Data
          </h2>
          <p>
            Easy-Peasy.AI may request access to your Google user data (including
            data from Google Workspace APIs) solely to provide functionality
            within our Website/Services. We use this data only to facilitate
            user authentication (sign-in and sign-up), retrieve calendar
            availability, and create new calendar events as part of our AI
            assistant bot functionality. This integration enables Our bots to
            schedule meetings and manage calendar events on Your behalf. We do
            not use Google user data for any purposes beyond these specific
            features.
          </p>
          <p>
            We do not share, transfer, or sell Google user data to third
            parties, except as necessary to provide or improve our Services,
            comply with the law, or protect Our rights. When We do share data
            for these purposes, We ensure it remains protected by
            confidentiality, integrity, and security measures aligned with
            industry-standard protections.
          </p>
          <p>
            Any sensitive or personal data obtained through Google Workspace
            APIs is encrypted both at rest and in transit. We maintain strict
            access controls and use recognized security best practices (such as
            TLS/SSL encryption). We also perform regular security assessments to
            safeguard the confidentiality of Your data.
          </p>
          <p>
            Easy-Peasy.AI does <strong>not</strong> retain or use Google
            Workspace data for training generalized AI or machine learning
            models. Data obtained via the Google APIs is used strictly to
            provide functionality within our Services, and we remain committed
            to protecting your privacy and data.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Information about General Data Protection Regulation (GDPR)
          </h2>
          <p>
            We may be collecting and using information from you if you are from
            the European Economic Area (EEA), and in this section of our Privacy
            Policy we are going to explain exactly how and why is this data
            collected, and how we maintain this data under protection from being
            replicated or used in the wrong way.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            What is GDPR?
          </h2>
          <p>
            GDPR is an EU-wide privacy and data protection law that regulates
            how EU residents&apos; data is protected by companies and enhances
            the control the EU residents have, over their personal data.
          </p>
          <p>
            The GDPR is relevant to any globally operating company and not just
            the EU-based businesses and EU residents. Our customers&apos; data
            is important irrespective of where they are located, which is why we
            have implemented GDPR controls as our baseline standard for all our
            operations worldwide.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Under GDPR, what is personal data?
          </h2>
          <p>
            Any data that relates to an identifiable or identified individual.
            GDPR covers a broad spectrum of information that could be used on
            its own, or in combination with other pieces of information, to
            identify a person. Personal data extends beyond a person&apos;s name
            or email address. Some examples include financial information,
            political opinions, genetic data, biometric data, IP addresses,
            physical address, sexual orientation, and ethnicity.
          </p>
          <p>The Data Protection Principles include requirements such as:</p>
          <ul>
            <li>
              Personal data collected must be processed in a fair, legal, and
              transparent way and should only be used in a way that a person
              would reasonably expect.
            </li>
            <li>
              Personal data should only be collected to fulfil a specific
              purpose and it should only be used for that purpose. Organizations
              must specify why they need the personal data when they collect it.
            </li>
            <li>
              Personal data should be held no longer than necessary to fulfil
              its purpose.
            </li>
            <li>
              People covered by the GDPR have the right to access their own
              personal data. They can also request a copy of their data, and
              that their data be updated, rectified, deleted, restricted,
              objected to or moved to another organization.
            </li>
          </ul>
          <p>
            If you are from the European Economic Area (EEA), please note that
            we will process your personal information where there are one or
            more lawful bases to do so under the GDPR, particularly where:
          </p>
          <ul>
            <li>
              You have provided your personal consent to Us with regard to the
              processing of your personal information by Us;
            </li>
            <li>
              The processing of Your personal information is necessary for the
              performance of the contract which You have entered into with Us;
            </li>
            <li>
              The processing of Your personal information is necessary to comply
              with Our legal or regulatory obligations under applicable law;
            </li>
            <li>
              The processing of Your personal information is necessary to
              protect the vital interests of a natural person, whether such
              person is You or another person;
            </li>
            <li>
              The processing of Your personal information is necessary for the
              performance of a task carried out in the public interest or
              exercise of official authority on Our part; or
            </li>
            <li>
              The processing of Your personal information is necessary for the
              purposes of Our legitimate interests or another natural
              person&apos;s legitimate interests.
            </li>
          </ul>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Why is GDPR important?
          </h2>
          <p>
            GDPR adds some new requirements regarding how companies should
            protect individuals&apos; personal data that they collect and
            process. It also raises the stakes for compliance by increasing
            enforcement and imposing greater fines for breach. Beyond these
            facts it&apos;s simply the right thing to do. At Easy-Peasy.AI we
            strongly believe that your data privacy is very important and we
            already have solid security and privacy practices in place that go
            beyond the requirements of this new regulation.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Representative
          </h2>
          <p>
            We value your privacy and your rights as a data subject and have
            therefore appointed Prighter Group with its local partners as our
            privacy representative and your point of contact for the following
            regions:
          </p>
          <p>
            <strong>European Union (EU)</strong>
          </p>
          <p>
            Prighter gives you an easy way to exercise your privacy-related
            rights (e.g. requests to access or erase personal data). If you want
            to contact us via our representative, Prighter or make use of your
            data subject rights, please visit the following website:{' '}
            <a href='https://app.prighter.com/portal/easypeasyai' target='_blank' rel='noopener noreferrer'>
              https://app.prighter.com/portal/easypeasyai
            </a>{' '}
            and please go through the privacy policy indicated therein in order
            to understand your applicable rights.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            California Residents
          </h2>
          <p>
            The California Consumer Privacy Act (CCPA) and the California
            Privacy Rights Act (CPRA) requires us to disclose categories of
            Personal Information we collect and how we use it, the categories of
            sources from whom we collect Personal Information, and the third
            parties with whom we share it, which we have explained above.
          </p>
          <p>
            We are also required to communicate information about rights
            California residents have under California law. If the CCPA and CPRA
            are applicable to You, You may exercise the following rights:
          </p>
          <ul>
            <li>
              <strong>Right to Know and Access.</strong> You may submit a
              verifiable request for information regarding the: (1) categories
              of Personal Information we collect, use, or share; (2) purposes
              for which categories of Personal Information are collected or used
              by us; (3) categories of sources from which we collect Personal
              Information; and (4) specific pieces of Personal Information we
              have collected about you.
            </li>
            <li>
              <strong>Right to Opt-Out.</strong> You have the right to opt out
              of the sale or sharing of your personal information at any time.
            </li>
            <li>
              <strong>Right to Non-Discrimination.</strong> You have the right
              to not be discriminated against for exercising any of your rights
              under the CCPA.
            </li>
            <li>
              <strong>Right to Correction.</strong> You have the right to
              correct inaccurate personal information that we may be storing
              and/or processing.
            </li>
            <li>
              <strong>
                Right to Limit Use and Disclosure of Sensitive Personal
                Information.
              </strong>{' '}
              You have the right to limit the use and disclosure of your
              personal information.
            </li>
            <li>
              <strong>Right to Opt-Out of Automated Decision Making.</strong>{' '}
              You have the right to refuse the automated processing of your
              personal information. You further have the right to further
              information about our automated decision-making technology and how
              it processes your personal information.
            </li>
            <li>
              <strong>Right to Delete.</strong> You may submit a verifiable
              request to close your account and we will delete Personal
              Information about you that we have collected.
            </li>
          </ul>
          <p>
            If you make a request, we have one month to respond to you. If you
            would like to exercise any of these rights, please contact us.
          </p>
          <p>
            Please note that we do not sell or share the Personal Information of
            our users.
          </p>
          <p>For more information about these rights, please contact us.</p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            California Online Privacy Protection Act (CalOPPA)
          </h2>
          <p>
            CalOPPA requires us to disclose categories of Personal Information
            we collect and how we use it, the categories of sources from whom we
            collect Personal Information, and the third parties with whom we
            share it, which we have explained above.
          </p>
          <p>CalOPPA users have the following rights:</p>
          <ul>
            <li>
              <strong>Right to Know and Access.</strong> You may submit a
              verifiable request for information regarding the: (1) categories
              of Personal Information we collect, use, or share; (2) purposes
              for which categories of Personal Information are collected or used
              by us; (3) categories of sources from which we collect Personal
              Information; and (4) specific pieces of Personal Information we
              have collected about you.
            </li>
            <li>
              <strong>Right to Equal Service.</strong> We will not discriminate
              against you if you exercise your privacy rights.
            </li>
            <li>
              <strong>Right to Delete.</strong> You may submit a verifiable
              request to close your account and we will delete Personal
              Information about you that we have collected.
            </li>
            <li>
              <strong>
                Right to request that a business that sells a consumer&apos;s
                personal data, not sell the consumer&apos;s personal data.
              </strong>
            </li>
          </ul>
          <p>
            If you make a request, we have one month to respond to you. If you
            would like to exercise any of these rights, please contact us.
          </p>
          <p>We do not sell the Personal Information of our users.</p>
          <p>For more information about these rights, please contact us.</p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Health Insurance Portability and Accountability Act (HIPAA)
          </h2>
          <p>
            In the event that the Health Insurance Portability and
            Accountability Act (HIPAA) is applicable to you and that You are a
            Covered Entity under the HIPAA, please note all of the following:
          </p>
          <ul>
            <li>
              You wholly consent to Us sending the Personal Health Information
              (PHI) to our necessary service providers;
            </li>
            <li>
              You undertake that You will not (under any circumstances
              whatsoever) disclose any information that would enable the
              individual identification of any of Your patients;
            </li>
            <li>
              You acknowledge that we are providing services to assist in
              formatting information (provided by You) into a specific protocol
              or format and that the only way for Us to obtain any personally
              identifiable information is through Your input; You further
              acknowledge that there is no necessity for You to provide any
              personally identifiable information for the purposes above;
            </li>
            <li>
              You undertake not to include any names, phone numbers, email
              addresses, Medicare Beneficiary Numbers, and biometric identifiers
              of any of your patients in any forms on our Website/Services;
            </li>
            <li>
              You expressly waive Your rights to make any claims against Us in
              the event that You are in breach of the above or provide any
              personally identifiable information to Us.
            </li>
          </ul>
          <p>
            Notwithstanding the above, kindly note that Easy-Peasy.AI Pte Ltd is
            a HIPAA certified Business Associate.
          </p>

          <h2 className='mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            Contact Us
          </h2>
          <p>Don&apos;t hesitate to contact us if you have any questions.</p>
          <ul>
            <li>Via Email: support@easy-peasy.ai</li>
            <li>
              Via this Link:{' '}
              <a href='https://easy-peasy.ai/contact' target='_blank' rel='noopener noreferrer'>
                https://easy-peasy.ai/contact
              </a>
            </li>
          </ul>
        </div>
      </section>
      {!isFromApp && <Footer />}
    </main>);
};
// Server-rendered (like terms-of-use) so `?from=app` is known at render time.
// As a statically optimised page the query was empty on the server, which made
// the marketing nav — and its /pricing link — flash into the in-app WebView.
const getServerSideProps: GetServerSideProps = async () => {
    return { props: {} };
};
export default PrivacyPolicy;

    async function __Next_Translate__getServerSideProps__1a11b03d20e__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/privacy-policy',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a11b03d20e__ as getServerSideProps }
  